<template>
  <PublicLayout page-title="Blog">
    <!-- Hero Section -->
    <section class="relative py-10 overflow-hidden">
      <!-- Background -->
      <div class="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-accent-50/20" />
      <div class="absolute top-0 right-0 w-96 h-96 bg-accent-200/20 rounded-full blur-3xl" />
      <div class="absolute bottom-0 left-0 w-64 h-64 bg-teal-200/15 rounded-full blur-3xl" />

      <div class="relative max-w-6xl mx-auto px-6">
        <!-- Badge -->
        <div class="inline-flex items-center gap-2 px-4 py-2 bg-accent-50 border border-accent-200 rounded-full text-accent-700 text-sm font-medium mb-6 animate-fade-up">
          <BookOpenIcon class="w-4 h-4" />
          Blog de Olvidadme
        </div>

        <!-- Title -->
        <h1 class="text-5xl md:text-6xl font-bold text-text-primary tracking-tight mb-4 animate-fade-up delay-100">
          Recursos sobre
          <span class="bg-gradient-to-r from-accent-600 to-teal-500 bg-clip-text text-transparent">
            privacidad digital
          </span>
        </h1>

        <p class="text-xl text-text-secondary max-w-2xl animate-fade-up delay-200">
          Guías, tutoriales y noticias sobre el derecho al olvido y protección de datos.
        </p>
      </div>
    </section>

    <!-- Main Content -->
    <div class="max-w-6xl mx-auto px-6 py-12">
      <!-- Loading State -->
      <div v-if="loading" class="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div v-for="n in 6" :key="n" class="bg-white rounded-2xl p-6 border border-slate-200 shadow-lg">
          <div class="animate-pulse">
            <div class="h-48 bg-slate-200 rounded-xl mb-4" />
            <div class="h-6 bg-slate-200 rounded w-3/4 mb-2" />
            <div class="h-4 bg-slate-200 rounded w-full mb-2" />
            <div class="h-4 bg-slate-200 rounded w-2/3" />
          </div>
        </div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-2xl p-6">
        <div class="flex">
          <ExclamationTriangleIcon class="w-5 h-5 text-red-400 mr-3 mt-0.5" />
          <div>
            <h3 class="text-sm font-medium text-red-800">Error al cargar los artículos</h3>
            <p class="text-sm text-red-700 mt-1">{{ error }}</p>
            <button
              @click="loadPosts"
              class="mt-3 text-sm font-medium text-red-800 hover:text-red-900 underline"
            >
              Intentar de nuevo
            </button>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else-if="!posts.length" class="text-center py-16">
        <div class="w-20 h-20 mx-auto mb-6 bg-slate-100 rounded-2xl flex items-center justify-center">
          <DocumentTextIcon class="w-10 h-10 text-slate-400" />
        </div>
        <h3 class="text-xl font-bold text-text-primary mb-2">No hay artículos</h3>
        <p class="text-text-secondary max-w-md mx-auto">
          Aún no hay artículos publicados en el blog.
        </p>
      </div>

      <!-- Posts -->
      <div v-else class="space-y-8">
        <!-- Uniform Grid -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <BlogCard
            v-for="(post, index) in posts"
            :key="post.id"
            :post="post"
            class="animate-fade-up"
            :style="{ animationDelay: `${index * 80}ms` }"
          />
        </div>

        <!-- Pagination -->
        <div v-if="totalPages > 1" class="mt-12">
          <div class="bg-white rounded-2xl border border-slate-200 shadow-lg px-6 py-4">
            <div class="flex items-center justify-between">
              <div class="text-sm text-text-secondary">
                Página {{ currentPage }} de {{ totalPages }}
              </div>

              <div class="flex items-center gap-2">
                <button
                  @click="goToPage(currentPage - 1)"
                  :disabled="currentPage === 1"
                  class="p-2 text-slate-600 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeftIcon class="w-4 h-4" />
                </button>

                <div class="flex items-center gap-1">
                  <button
                    v-for="page in visiblePages"
                    :key="page"
                    @click="goToPage(page)"
                    :class="[
                      'px-3 py-2 text-sm font-medium rounded-xl transition-colors',
                      page === currentPage
                        ? 'bg-accent-500 text-white'
                        : 'text-slate-600 hover:bg-slate-50'
                    ]"
                  >
                    {{ page }}
                  </button>
                </div>

                <button
                  @click="goToPage(currentPage + 1)"
                  :disabled="currentPage === totalPages"
                  class="p-2 text-slate-600 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRightIcon class="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </PublicLayout>
</template>

<script setup>
import { ref, onMounted, nextTick, watch } from 'vue'
import { signalPrerenderReady } from '@/composables/usePrerenderReady'
import { useRoute, useRouter } from 'vue-router'
import { useSeo } from '@/composables/useSeo.js'
import { useBlogPagination } from '@/composables/useBlogPagination.js'
import PublicLayout from '@/components/layout/PublicLayout.vue'
import BlogCard from '@/components/blog/BlogCard.vue'
import blogService from '@/services/blogService'
import {
  BookOpenIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/vue/24/outline'

const route = useRoute()
const router = useRouter()

const loading = ref(false)
const error = ref(null)
const posts = ref([])

const currentPage = ref(parseInt(route.query.page) || 1)
const pageSize = ref(12)

const totalPages = ref(0)

useSeo({
  title: 'Blog - Olvidadme | Derecho al Olvido y Privacidad Digital',
  description: 'Guías, noticias y recursos sobre el derecho al olvido, privacidad digital y protección de datos personales.',
  canonical: '/blog'
})

const { visiblePages } = useBlogPagination(currentPage, totalPages)

const loadPosts = async () => {
  try {
    loading.value = true
    error.value = null

    const params = {
      page: currentPage.value,
      limit: pageSize.value,
      status: 'published'
    }

    const response = await blogService.getPosts(params)

    posts.value = response.posts || []
    totalPages.value = response.totalPages || 0
  } catch (err) {
    console.error('Error loading posts:', err)
    error.value = 'Error al cargar los artículos'
  } finally {
    loading.value = false
  }
}

const goToPage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page
    syncUrl()
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}

const syncUrl = () => {
  const query = {}

  if (currentPage.value !== 1) query.page = currentPage.value.toString()

  return router.replace({ query })
}

onMounted(async () => {
  await loadPosts()
  nextTick(() => signalPrerenderReady())
})

watch(() => route.query, () => {
  currentPage.value = parseInt(route.query.page) || 1

  loadPosts()
}, { deep: true })
</script>

<style scoped>
@reference "../../assets/styles/main.css";

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

button:focus,
select:focus {
  outline: 2px solid theme('colors.accent.500');
  outline-offset: 2px;
}

@media (prefers-reduced-motion: reduce) {
  .animate-fade-up {
    animation: none;
  }
}
</style>
